import type { ScoreCardViewData } from '@shared/types/views';
import { ArrowRight } from 'lucide-react';
import { StatusBadge, statusLabel } from '@/components/views/atoms/StatusBadge';
import { ViewActionButton } from '@/components/views/atoms/ViewActionButton';
import { CommunicationCue } from '@/components/views/atoms/CommunicationCue';
import {
  DecisionBand,
  LabelValueList,
  MeterBar,
  ON_BAND_ACTION_CLASS,
  VIEW_TEXT,
} from '@/components/views/atoms/view-grammar';
import { ExpandableText } from '@/components/ui/ExpandableText';
import { cn } from '@/lib/utils';

interface ScoreCardViewProps {
  hideTitle?: boolean;
  data: ScoreCardViewData;
}

/** The maximum a label states: « /10 », « sur 6 », « of 5 », or a percentage. */
const parseMaxFromLabel = (label?: string): number | null => {
  if (!label) return null;
  if (/%/.test(label)) return 100;
  const match = label.match(/(?:\/|\bsur\b|\bof\b)\s*(\d+(?:[.,]\d+)?)/i);
  if (!match?.[1]) return null;
  const parsed = Number(match[1].replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const formatCompactNumber = (value: number): string => (
  Number.isInteger(value) ? String(value) : value.toFixed(1)
);

const getScoreMax = (data: Pick<ScoreCardViewData, 'score' | 'score_label' | 'segments'>): number => (
  data.segments?.find((segment) => segment.max !== undefined)?.max
  ?? parseMaxFromLabel(data.score_label)
  ?? (data.score !== undefined && data.score > 10 ? 100 : 10)
);

/** French numbers in a label the model wrote: « 5.0/10 » reads « 5/10 », « 7.5 » reads « 7,5 ». */
const frenchNumbers = (label: string): string =>
  label.replace(/(\d+)[.,]0(?!\d)/g, '$1').replace(/(\d)\.(\d)/g, '$1,$2');

/**
 * The score is always shown; the label completes it (« 64 % atteint »,
 * « 3 critères sur 6 »). A label that already carries a number (« 7/10 ») is
 * shown as is, in French.
 */
export const getScoreLabel = (data: Pick<ScoreCardViewData, 'score' | 'score_label' | 'segments'>): string | null => {
  if (data.score === undefined) return data.score_label ? frenchNumbers(data.score_label) : null;
  const score = formatCompactNumber(data.score).replace('.', ',');
  if (!data.score_label) return `${score}/${formatCompactNumber(getScoreMax(data)).replace('.', ',')}`;
  if (/^\s*[\d.,]/.test(data.score_label)) return frenchNumbers(data.score_label.trim());
  // A no-break space keeps « 64 % » together.
  return `${score} ${data.score_label.trim()}`;
};

const normalizeText = (value: string): string => value.replace(/\s+/g, ' ').trim().toLowerCase();

/** Older payloads copied the verdict into the cue; the card shows it once. */
const repeatsInsight = (body: string, insight?: string): boolean => {
  if (!insight) return false;
  const cue = normalizeText(body).replace(/[.…]+$/, '');
  return cue.length > 0 && normalizeText(insight).startsWith(cue);
};

/**
 * One surface, the shared grammar inside: a verdict leads with the decision
 * band (score and state, the sentence, its action), then the facts as
 * label/value rows, the score details on the one bar, and what comes next.
 */
export const ScoreCardView = ({ data, hideTitle }: ScoreCardViewProps) => {
  const scoreLabel = getScoreLabel(data);
  const allSegments = data.segments ?? [];
  const visibleSegments = allSegments.length > 1 || data.score === undefined ? allSegments : [];
  const communication = data.communication && !repeatsInsight(data.communication.body, data.insight)
    ? data.communication
    : null;
  const actions = data.actions ?? [];
  const status = data.status !== 'neutral' ? statusLabel(data.status) : null;
  const context = [hideTitle ? null : data.title, data.subtitle].filter(Boolean).join(' · ') || null;
  const badge = [scoreLabel, status].filter(Boolean).join(' · ') || null;
  const primaryAction = data.primaryAction
    ? <ViewActionButton action={data.primaryAction} className={data.insight ? ON_BAND_ACTION_CLASS : undefined} />
    : null;

  return (
    <div data-view-surface className="flex flex-col gap-6 rounded-2xl border border-domino-cardBorder bg-domino-warm p-6 lg:p-8">
      {data.insight ? (
        <DecisionBand badge={badge} context={context} action={primaryAction}>
          <ExpandableText text={data.insight} collapsedLines={4} minCharactersToToggle={260} className="text-white" buttonClassName="text-domino-sun hover:text-white" />
        </DecisionBand>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 flex-col gap-1">
              {!hideTitle && data.title && <p className={VIEW_TEXT.title}>{data.title}</p>}
              {data.subtitle && <p className={VIEW_TEXT.detail}>{data.subtitle}</p>}
            </div>
            {data.status !== 'neutral' && <StatusBadge status={data.status} />}
          </div>
          {scoreLabel && (
            <div className="flex max-w-[220px] flex-col gap-2.5">
              <p className={cn('font-semibold tabular-nums tracking-tight text-domino-ink', scoreLabel.length > 10 ? 'text-2xl' : 'text-[32px] leading-9')}>{scoreLabel}</p>
              {data.score !== undefined && <MeterBar value={data.score} max={getScoreMax(data)} />}
            </div>
          )}
        </div>
      )}

      {data.facts && data.facts.length > 0 && (
        <LabelValueList
          columns={2}
          rows={data.facts.map((fact) => ({ label: fact.label, value: fact.value, tone: fact.status ?? 'neutral' }))}
        />
      )}

      {visibleSegments.length > 0 && (
        <div className="border-t border-domino-border">
          {visibleSegments.map((segment) => {
            const max = segment.max ?? 10;
            return (
              <div key={segment.label} className="grid grid-cols-[148px_minmax(0,1fr)_56px] items-center gap-4 border-b border-domino-border py-3">
                <span className={cn('truncate', VIEW_TEXT.detail)} title={segment.label}>{segment.label}</span>
                <MeterBar value={segment.value} max={max} />
                <span className="text-right text-[15px] font-medium tabular-nums text-domino-ink">
                  {formatCompactNumber(segment.value).replace('.', ',')}/{formatCompactNumber(max)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {(communication || actions.length > 0 || (primaryAction && !data.insight)) && (
        <div className="flex flex-col gap-3">
          {communication && <CommunicationCue cue={communication} />}
          {actions.length > 0 && (
            <ul className="flex flex-col">
              {actions.map((action) => (
                <li key={action.label} className={cn('flex items-start gap-3 border-b border-domino-border py-3 last:border-b-0', VIEW_TEXT.body)}>
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-domino-aubergine" />
                  <span className="min-w-0 break-words">{action.label}</span>
                </li>
              ))}
            </ul>
          )}
          {primaryAction && !data.insight && <div>{primaryAction}</div>}
        </div>
      )}
    </div>
  );
};
